import {
  Accessor,
  createContext,
  onMount,
  createSignal,
  JSX,
  Setter,
  Show,
  useContext,
  Switch,
  Match,
} from "solid-js";
import { createStore } from "solid-js/store";
import { Product, SearchResults } from "~/server/types/search";
import { WebStoreRouteData } from "~/server/data/store_route_data";
import { V4HomeResponse } from "~/data/v4_homepage";
import { clientRepo } from "~/server/apis/client_repo";
import { CustomHeaderOldDS } from "~/components/header/header_webstore";
import { logIntoHubble } from "~/data/classic/logIntoHubble";
import {
  clientIdConfig,
  getMomentsV3PartnerConfig,
  MomentsV3PartnerConfig,
  useMomentsV3,
} from "~/data/partner_config";
import { Link, Title } from "@solidjs/meta";
import { Mode } from "~/types";
import { useAnalyticsService } from "./analytics_service_context";
import { HubbleEvent } from "~/data/events";
import { DottedLoader } from "~/widgets/loader";
import { FullScreenLoaderTelescope } from "~/components/webstore_telescope/full_screen_loader";
import { useSearchParams } from "@solidjs/router";
import { startWebstoreLoginFlow } from "~/flows/webstore_login_flow/webstore_login_flow";
import { HeaderTelescopeRnr } from "~/components/webstore_telescope/header_telescope/header_telescope_rnr";
import { HeaderTelescopeWebstore } from "~/components/webstore_telescope/header_telescope/header_telescope_webstore";
import { openIntercom } from "~/utils/third_party/intercom";
import { openModalV2 } from "~/shared_states/modal";
import { BuddyDealsContactSupportModal } from "~/components/rnr_partners_custom_components/buddydeals/buddydeals_contact_support_modal";
import { attachUserIdToSentry } from "~/utils/third_party/sentry";
import { OfferConstruct } from "~/server/types/brand";
import { isOfferAvailed } from "~/core/offer-construct/offer-construct";

type StoreProviderProps = {
  children: JSX.Element;
  routeData: Accessor<WebStoreRouteData | undefined>;
};

interface StoreState {
  brands: Product[];
  isLoggedIn: boolean;
  v4HomePage: V4HomeResponse | null;
  routeData: WebStoreRouteData | undefined;
  coins: number | undefined;
  offerConstruct: OfferConstruct | undefined;
}

function useWebStoreLayout(
  routeData: Accessor<WebStoreRouteData | undefined>
): WebStoreContextValue {
  const [isHeaderScrolledOut, setIsHeaderScrolledOut] = createSignal(false);
  const [storeState, setStoreState] = createStore<StoreState>({
    brands: [],
    isLoggedIn: false,
    v4HomePage: null,
    routeData: undefined,
    coins: undefined,
    offerConstruct: undefined,
  });

  const [queryParams, _setQueryParams] = useSearchParams();
  // get max brands from query params /
  const maxBrands = queryParams.maxBrands;

  const analyticsService = useAnalyticsService();

  const [isSearchActive, setIsSearchActive] = createSignal(false);

  // Design system version flag - initialized from useMomentsV3, toggleable in staging
  const [isDesignSystemV2Enabled, setIsDesignSystemV2Enabled] =
    createSignal(false);

  onMount(() => {
    let isLoggedIn = routeData()?.isLoggedIn ?? false;
    let v4HomePage = routeData()?.v4HomePage ?? null;
    let brands = routeData()?.brands as Product[];
    if (maxBrands) {
      brands = brands.slice(0, parseInt(maxBrands as string));
    }
    setStoreState("brands", brands);
    setStoreState("isLoggedIn", isLoggedIn);
    setStoreState("v4HomePage", v4HomePage);
    setStoreState("routeData", routeData());
    setStoreState("coins", routeData()?.v4HomePage?.coins.totalAvailable);

    // Fetch offer construct for OneCard clients
    const clientId = routeData()?.clientId ?? "";
    if (clientId === clientIdConfig.onecardClientId) {
      clientRepo
        .getOfferConstruct()
        .then((offerConstruct) => {
          setStoreState("offerConstruct", offerConstruct);
        })
        .catch(() => {
          // Ignore error, offerConstruct remains undefined
        });
    }

    // Initialize design system flag from useMomentsV3

    if (getMode() === "hubble") {
      // enable design system v2 for webstore in production and staging
      setIsDesignSystemV2Enabled(true);
    } else {
      setIsDesignSystemV2Enabled(useMomentsV3(clientId));
    }

    if (isLoggedIn) {
      analyticsService.setIdentity(v4HomePage?.user.id ?? "");
      attachUserIdToSentry(v4HomePage?.user.id ?? "");
    }
  });

  async function refreshV4HomePage() {
    const v4Home = await clientRepo.fetchV4Home();
    setStoreState("v4HomePage", v4Home);
    setStoreState("coins", v4Home.coins.totalAvailable);
  }

  async function loginToWebstore(onAfterLogin: () => void) {
    if (storeState.isLoggedIn) {
      return;
    }
    const _afterLogin = async () => {
      const v4Home$Promise = clientRepo.fetchV4Home();
      const brands$Promise = clientRepo.getProductSearch({
        query: "",
        pagination: { pageNo: 0, limit: 30 },
      });
      const [v4Home, brands] = await Promise.all([
        v4Home$Promise,
        brands$Promise,
      ]);
      const activeBrands = getActiveBrandsFromSearchResponse(brands);
      setStoreState("brands", activeBrands);
      setStoreState("v4HomePage", v4Home);
      setStoreState("coins", v4Home.coins.totalAvailable);
      setStoreState("isLoggedIn", true);
      analyticsService.setIdentity(v4Home.user.id);
      analyticsService.logEvent(HubbleEvent.LOGIN, {
        phoneNumber: v4Home.user.phoneNumber,
      });
      attachUserIdToSentry(v4Home.user.id);
      onAfterLogin();
    };

    if (isDesignSystemV2Enabled()) {
      startWebstoreLoginFlow(_afterLogin);
    } else {
      logIntoHubble(_afterLogin);
    }
  }

  function getActiveBrandsFromSearchResponse(
    searchBrandsResponse: SearchResults
  ) {
    const brands: Product[] = [];
    searchBrandsResponse.data.forEach((section) => {
      section.products.forEach((product) => {
        if (product.voucherProduct.status === "ACTIVE") {
          brands.push(product);
        }
      });
    });
    return brands;
  }

  function webstoreUIConfig(): WebstoreUIConfig {
    let routeData = storeState.routeData;

    function showBranding() {
      if (routeData?.clientId == clientIdConfig.buddydealsClientId) {
        return false;
      }
      return routeData?.mode === "rnr";
    }

    function showCoins() {
      if (
        getClientId() == clientIdConfig.axisClientId ||
        getClientId() == clientIdConfig.nielsenClientId ||
        getClientId() == clientIdConfig.buddydealsClientId ||
        getClientId() == clientIdConfig.tdClientId ||
        getClientId() == clientIdConfig.raisoniClientId ||
        getClientId() == clientIdConfig.indriyaClientId ||
        getClientId() == clientIdConfig.iciciIndriyaClientId ||
        getClientId() == clientIdConfig.mastercardIndriyaClientId
      ) {
        return false;
      }
      return true;
    }

    return {
      showHubbleBranding: showBranding(),
      showCoins: showCoins(),
    };
  }

  function getPartnerConfigV3() {
    return getMomentsV3PartnerConfig(getClientId());
  }

  const getMode = () => {
    if (routeData()?.mode === "rnr") {
      return "rnr";
    }
    return "hubble";
  };

  const getClientId = () => {
    return routeData()?.clientId ?? "";
  };

  const checkIsOfferAvailed = () => {
    const offerConstruct = storeState.offerConstruct;
    if (!offerConstruct) {
      return false;
    }
    return isOfferAvailed(offerConstruct);
  };

  /**
   *
   * @param props { onLogout: () => void }
   * @description This function is used to logout the user from the webstore.

   */

  function onLogout(props: { onLogout: () => void }) {
    setStoreState("brands", []);
    setStoreState("v4HomePage", null);
    setStoreState("coins", undefined);
    setStoreState("isLoggedIn", false);

    // todo : logout in analytics service, sentry
    // analyticsService.setIdentity(v4Home.user.id);
    // analyticsService.logEvent(HubbleEvent.LOGIN, {
    //   phoneNumber: v4Home.user.phoneNumber,
    // });
  }

  function openContactSupport() {
    if (getClientId() === clientIdConfig.buddydealsClientId) {
      openModalV2(() => <BuddyDealsContactSupportModal />, {
        size: "small",
      });
      return;
    }

    const user = storeState.v4HomePage?.user;
    if (storeState.isLoggedIn && user) {
      const name = [user.firstName, user.lastName]
        .map((part) => part?.trim() ?? "")
        .filter(Boolean)
        .join(" ");
      openIntercom({
        user_id: user.id,
        ...(name ? { name } : {}),
        ...(user.email ? { email: user.email } : {}),
      });
      return;
    }

    openIntercom();
  }

  return {
    storeState,
    loginToWebstore,
    refreshV4HomePage,
    webstoreUIConfig,
    getMode,
    getClientId,
    checkIsOfferAvailed,
    setIsSearchActive,
    getPartnerConfigV3,
    isSearchActive,
    isHeaderScrolledOut,
    setIsHeaderScrolledOut,
    isDesignSystemV2Enabled,
    setIsDesignSystemV2Enabled,
    onLogout,
    openContactSupport,
  };
}

type WebstoreUIConfig = {
  showHubbleBranding: boolean;
  showCoins: boolean;
};

type WebStoreContextValue = {
  storeState: StoreState;
  loginToWebstore: (onAfterLogin: () => void) => Promise<void>;
  refreshV4HomePage: () => Promise<void>;
  webstoreUIConfig: () => WebstoreUIConfig;
  getMode: () => Mode;
  getClientId: () => string;
  checkIsOfferAvailed: () => boolean;
  setIsSearchActive: Setter<boolean>;
  getPartnerConfigV3: () => MomentsV3PartnerConfig;
  isSearchActive: Accessor<boolean>;
  isHeaderScrolledOut: Accessor<boolean>;
  setIsHeaderScrolledOut: Setter<boolean>;
  isDesignSystemV2Enabled: Accessor<boolean>;
  setIsDesignSystemV2Enabled: Setter<boolean>;
  onLogout: (props: { onLogout: () => void }) => void;
  openContactSupport: () => void;
};

const WebStoreContext = createContext<WebStoreContextValue>();

export function WebStoreContextProvider(props: StoreProviderProps) {
  const contextValue = useWebStoreLayout(props.routeData);

  return (
    <WebStoreContext.Provider value={contextValue}>
      <Show when={contextValue.storeState.routeData?.mode === "rnr"}>
        <Title>{getRnrPageTitle()}</Title>
        <RnrPageIcon />
      </Show>

      <Show
        when={contextValue.storeState.routeData}
        fallback={
          contextValue.isDesignSystemV2Enabled() ? (
            <FullScreenLoaderTelescope />
          ) : (
            <DottedLoader />
          )
        }
      >
        <div class="h-max w-full ">
          <Switch>
            <Match
              when={
                contextValue.getClientId() === clientIdConfig.axisClientId ||
                contextValue.getClientId() === clientIdConfig.indriyaClientId ||
                contextValue.getClientId() ===
                  clientIdConfig.iciciIndriyaClientId ||
                contextValue.getClientId() === clientIdConfig.tdClientId
              }
            >
              <CustomHeaderOldDS
                authMethod={
                  contextValue.storeState.routeData?.clientConfig?.authMethod ??
                  "PHONE"
                }
                headerImageUrl={
                  contextValue.storeState.routeData?.clientConfig?.clientLogoUrl
                }
                isSearchActive={contextValue.isSearchActive}
                setIsSearchActive={contextValue.setIsSearchActive}
              />
            </Match>
            <Match when={true}>
              <Switch>
                <Match when={contextValue.storeState.routeData?.mode === "rnr"}>
                  <HeaderTelescopeRnr />
                </Match>
                <Match when={true}>
                  <HeaderTelescopeWebstore />
                </Match>
              </Switch>
            </Match>
          </Switch>
          {props.children}
        </div>
      </Show>
    </WebStoreContext.Provider>
  );

  function getRnrPageTitle() {
    if (contextValue.getPartnerConfigV3().pageTitle) {
      return contextValue.getPartnerConfigV3().pageTitle;
    }
    return "Gifts & Rewards - By Hubble";
  }

  function RnrPageIcon() {
    if (contextValue.getPartnerConfigV3().pageIcon) {
      return (
        <Link rel="icon" href={contextValue.getPartnerConfigV3().pageIcon} />
      );
    }
    return <div></div>;
  }
}

export function useWebStoreContext() {
  const context = useContext(WebStoreContext);
  if (!context) {
    throw new Error(
      "useWebStoreContext must be used within a WebStoreContextProvider"
    );
  }
  return context;
}
